import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../types/vehicle';
import FleetAvailabilityTimeline from './FleetAvailabilityTimeline';
import FleetAvailabilityWeekView from './FleetAvailabilityWeekView';
import { addDays, subDays, format, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ReadOnlyCalendarModalProps {
  open: boolean;
  onClose: () => void;
  fleetId: string;
  fleetName: string;
  vehicles: Vehicle[];
  selectedVehicleId?: string; // Pour focus sur un véhicule spécifique
}

type ViewMode = 'day' | 'week';

const ReadOnlyCalendarModal: React.FC<ReadOnlyCalendarModalProps> = ({
  open,
  onClose,
  fleetId,
  fleetName,
  vehicles,
  selectedVehicleId
}) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('day');

  const handlePrevious = () => {
    if (viewMode === 'day') {
      setCurrentDate(subDays(currentDate, 1));
    } else {
      setCurrentDate(subDays(currentDate, 7));
    }
  };

  const handleNext = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 7));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const formatDateDisplay = () => {
    if (viewMode === 'day') {
      return format(currentDate, 'EEEE dd MMMM yyyy', { locale: fr });
    } else {
      // Vue semaine : afficher du lundi au dimanche
      const weekStart = startOfWeek(currentDate, { locale: fr, weekStartsOn: 1 }); // 1 = lundi
      const weekEnd = endOfWeek(currentDate, { locale: fr, weekStartsOn: 1 });
      return `${format(weekStart, 'dd MMM', { locale: fr })} - ${format(weekEnd, 'dd MMM yyyy', { locale: fr })}`;
    }
  };

  // Filtrer pour afficher uniquement le véhicule sélectionné si spécifié
  const displayedVehicles = selectedVehicleId
    ? vehicles.filter(v => v.id === selectedVehicleId)
    : vehicles;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="span">
              {t('reservation:calendar.title', 'Calendrier (lecture seule)')}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {fleetName}
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Note explicative */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'reservation:calendar.explanation',
              'Affichage des disponibilités en lecture seule. Les créneaux occupés sont anonymisés pour respecter la confidentialité.'
            )}
          </Typography>
        </Box>

        {/* Contrôles de navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton onClick={handlePrevious} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 250, textAlign: 'center' }}>
              {formatDateDisplay()}
            </Typography>
            <IconButton onClick={handleNext} size="small">
              <ArrowForwardIcon />
            </IconButton>
            <Button
              variant="outlined"
              size="small"
              onClick={handleToday}
              sx={{ ml: 2, textTransform: 'none' }}
            >
              {t('reservation:calendar.today', 'Aujourd\'hui')}
            </Button>
          </Box>

          {/* Toggle vue jour/semaine */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="day">
              {t('reservation:calendar.day', 'Jour')}
            </ToggleButton>
            <ToggleButton value="week">
              {t('reservation:calendar.week', 'Semaine')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Timeline */}
        {viewMode === 'day' ? (
          <FleetAvailabilityTimeline
            fleetId={fleetId}
            vehicles={displayedVehicles}
            date={currentDate}
            startHour={8}
            endHour={20}
          />
        ) : (
          <FleetAvailabilityWeekView
            fleetId={fleetId}
            vehicles={displayedVehicles}
            weekStartDate={startOfWeek(currentDate, { locale: fr, weekStartsOn: 1 })}
            startHour={8}
            endHour={20}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('reservation:common.close', 'Retour')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReadOnlyCalendarModal;
